/**
 * <s-welcome-bag> — first-visit welcome dialog with a quick-add shopping bag.
 *
 * Opens its child <dialog> once per browser session after a short delay.
 * Closing is native (Invoker Commands / Escape); this element only adds
 * backdrop-click closing and closes itself when a product is added so the
 * cart drawer opened by <s-product-form> takes focus.
 *
 * Attributes:
 *   delay          — milliseconds before opening (default 1200)
 *   storage-key    — sessionStorage key used to remember it was shown
 *   disabled       — never auto-open (used in the theme editor)
 */
class SWelcomeBag extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    }, opts);

    this.addEventListener('submit', () => {
      // Let <s-product-form> read the form first, then step aside for the cart drawer.
      requestAnimationFrame(() => dialog.close());
    }, opts);

    if (this.hasAttribute('disabled')) return;

    const key = this.getAttribute('storage-key') || 'welcome-bag-shown';
    if (this.#wasShown(key)) return;

    const delay = Number.parseInt(this.getAttribute('delay') || '1200', 10);
    this.#timer = setTimeout(() => {
      if (dialog.open || document.querySelector('dialog[open]')) return;
      dialog.showModal();
      this.#markShown(key);
    }, Number.isFinite(delay) ? delay : 1200);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #wasShown(key) {
    try {
      return window.sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  #markShown(key) {
    try {
      window.sessionStorage.setItem(key, '1');
    } catch {
      // Storage can be unavailable (private mode); the dialog simply shows again next page.
    }
  }
}

if (!customElements.get('s-welcome-bag')) {
  customElements.define('s-welcome-bag', SWelcomeBag);
}
